import { Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { SearchIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { config } from "@/shared/lib/config";
import { LAO_MONTH_NAMES } from "@/shared/lib/date-time";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

type DepartmentItem = { id: string; name: string };

export type MonthlyReportSearchParams = {
  offset?: number;
  limit?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  periodYear?: number;
  periodMonth?: number;
  departmentId?: string;
  search?: string;
};

const VALUE_ALL = "__all__";

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i).map(
  (y) => ({ value: String(y), label: String(y) }),
);
const MONTH_OPTIONS = [
  { value: VALUE_ALL, label: "ທັງໝົດ" },
  ...LAO_MONTH_NAMES.map((name, i) => ({
    value: String(i + 1),
    label: name,
  })),
];

type MonthlyReportFilterProps = {
  searchParams: MonthlyReportSearchParams;
  fromRoute: "/app/management/monthly-reports";
};

export function MonthlyReportFilter({
  searchParams,
  fromRoute,
}: MonthlyReportFilterProps) {
  const nav = useNavigate({ from: fromRoute });
  const search = searchParams;

  const [searchInput, setSearchInput] = useState(search.search ?? "");
  const [yearInput, setYearInput] = useState<string>(
    search.periodYear != null ? String(search.periodYear) : VALUE_ALL,
  );
  const [monthInput, setMonthInput] = useState<string>(
    search.periodMonth != null ? String(search.periodMonth) : VALUE_ALL,
  );
  const [departmentInput, setDepartmentInput] = useState(
    search.departmentId ?? "",
  );

  useEffect(() => {
    setSearchInput(search.search ?? "");
    setYearInput(
      search.periodYear != null ? String(search.periodYear) : VALUE_ALL,
    );
    setMonthInput(
      search.periodMonth != null ? String(search.periodMonth) : VALUE_ALL,
    );
    setDepartmentInput(search.departmentId ?? "");
  }, [
    search.search,
    search.periodYear,
    search.periodMonth,
    search.departmentId,
  ]);

  const limit = search.limit ?? 20;
  const yearSelectOptions = [
    { value: VALUE_ALL, label: "ທັງໝົດ" },
    ...YEAR_OPTIONS,
  ];

  const apply = (
    opts: Partial<{
      searchVal: string;
      yearVal: string;
      monthVal: string;
      departmentVal: string;
    }>,
  ) => {
    const searchTerm = (opts.searchVal ?? searchInput).trim() || undefined;
    const periodYear =
      (opts.yearVal ?? yearInput) === VALUE_ALL
        ? undefined
        : Number(opts.yearVal ?? yearInput);
    const periodMonth =
      (opts.monthVal ?? monthInput) === VALUE_ALL
        ? undefined
        : Number(opts.monthVal ?? monthInput);
    const departmentId =
      (opts.departmentVal ?? departmentInput).trim() || undefined;

    nav({
      search: {
        offset: 0,
        limit,
        sort: search.sort,
        periodYear: Number.isFinite(periodYear) ? periodYear : undefined,
        periodMonth: Number.isFinite(periodMonth) ? periodMonth : undefined,
        departmentId,
        search: searchTerm,
      },
    });
  };

  const applySearch = (searchVal: string) => {
    setSearchInput(searchVal);
    apply({ searchVal });
  };
  const debouncedApplySearch = useDebounceCallback(applySearch, 400);

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 px-2">
      <div className="relative min-w-[200px] flex-1">
        <SearchIcon className="absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          id="monthly-report-search"
          aria-label="ຄົ້ນຫາຕາມຊື່ ຫຼື ຄຳອະທິບາຍ"
          placeholder="ຄົ້ນຫາຊື່ ຫຼື ຄຳອະທິບາຍ..."
          className="h-8 pl-8 sm:max-w-2xs"
          value={searchInput}
          onChange={(e) => debouncedApplySearch(e.target.value)}
          onKeyDown={(e) =>
            e.key === "Enter" && applySearch(searchInput)
          }
        />
      </div>
      <SimpleSelect
        options={yearSelectOptions}
        value={yearInput}
        onValueChange={(v) => {
          setYearInput(v);
          apply({ yearVal: v });
        }}
        placeholder="ປີ"
        className="h-8 w-full sm:w-24"
        aria-label="ປີ"
      />
      <SimpleSelect
        options={MONTH_OPTIONS}
        value={monthInput}
        onValueChange={(v) => {
          setMonthInput(v);
          apply({ monthVal: v });
        }}
        placeholder="ເດືອນ"
        className="h-8 w-full sm:w-32"
        aria-label="ເດືອນ"
      />
      <InfiniteCombobox<DepartmentItem>
        queryKey={["departments", "filter-monthly-report"]}
        queryFn={(args) =>
          fetchLookupForInfinite(`${config.apiUrl}/departments/lookup`, args)
        }
        preloadQueryFn={(id) =>
          hydrateLookupItem(`${config.apiUrl}/departments/lookup`, id)
        }
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        value={departmentInput}
        onValueChange={(val) => {
          setDepartmentInput(val ?? "");
          apply({ departmentVal: val ?? "" });
        }}
        placeholder="ພະແນກ"
        className="h-8 sm:max-w-2xs"
      />
    </div>
  );
}
